import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { useAuthStore } from "./auth";
import { useSignalR } from "@/composables/signalr";
import {
  getApiV1Users,
  postApiV1Users,
  type CreateUserDto,
  type UserDto,
} from "@/client";
import type {
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
} from "@/types/signalr-events";

export const useUsersStore = defineStore("users", () => {
  const users = ref<UserDto[]>([]);
  const usersLoading = ref(false);
  const usersLoaded = ref(false);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (usersLoaded.value) {
      fetchUsers().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });

  const user = computed(() =>
    users.value.find((u) => u.id === useAuthStore().user?.userId),
  );

  async function fetchUsers() {
    usersLoading.value = true;
    try {
      const response = await getApiV1Users();
      if (response.error) {
        console.error("Error fetching users:", response.error);
        throw new Error("Failed to fetch users");
      }
      users.value = response.data!;
      usersLoaded.value = true;
    } finally {
      usersLoading.value = false;
    }
  }

  async function createUser(user: CreateUserDto) {
    const response = await postApiV1Users({ body: user });
    if (response.error) {
      console.error("Error creating user:", response.error);
      throw new Error("Failed to create user");
    }
    await useAuthStore().refreshToken();
  }

  async function ensureUsersLoaded() {
    if (!usersLoaded.value) {
      await fetchUsers();
      console.log("Fetched users");
    }
  }
  //#region SignalR
  subscribe<EntityUpdatedEvent<UserDto>>("UserUpdated", async (event) => {
    console.debug("UserUpdated event received:", event);
    createOrUpdateUser(event.entity);
  });
  subscribe<EntityDeletedEvent>("UserDeleted", async (event) => {
    console.debug("UserDeleted event received:", event);
    const index = users.value.findIndex((user) => user.id === event.entityId);
    if (index !== -1) {
      users.value.splice(index, 1);
    }
  });
  subscribe<EntityCreatedEvent<UserDto>>("UserCreated", async (event) => {
    console.debug("UserCreated event received:", event);
    createOrUpdateUser(event.entity);
  });

  function createOrUpdateUser(user: UserDto) {
    const index = users.value.findIndex((u) => u.id === user.id);
    if (index === -1) {
      users.value.push(user);
    } else {
      users.value[index] = user;
    }
  }
  //#endregion

  return {
    user,
    users,
    usersLoading,
    usersLoaded,

    createUser,
    fetchUsers,
    ensureUsersLoaded,
  };
});
